import React, { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import { Option } from 'models/Global';
import FilterActionWrapper from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/FilterActionWrapper';
import SearchIcon from 'icons/Search.icon';
import { Input } from 'ncoded-component-library';
import useDebounceSearch from 'hooks/useDebounceSearch';
import useMatchMedia from 'hooks/useMatchMedia';
import { getHasElementsInArray, getJoinedWordsWithComma } from 'utils';
import { SearchableFilterValue } from 'models/Filters';
import { TextHighlighter } from 'components/TextHighlighter';
import useSearchableFilters from 'hooks/useSearchableFilters';

import './SearchableFilter.styles.scss';

type SearchableFilterProps = {
  className?: string;
  value?: SearchableFilterValue;
  triggerTitle?: string;
  isMultiple?: boolean;
  filterKey?: string;
  onSelect: (value: SearchableFilterValue) => void;
  getOptionsOnSearch: (searchString: string) => Promise<Option[]>;
};

const SearchableFilter: React.FC<SearchableFilterProps> = (props) => {
  const {
    className,
    value,
    filterKey,
    triggerTitle,
    isMultiple = false,
    onSelect,
    getOptionsOnSearch,
  } = props;

  const { isLoading, options, onSearch, resetState } =
    useSearchableFilters(getOptionsOnSearch);

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);

  const classes = classNames('searchable-filter', className);

  const isPhablet = useMatchMedia('isPhablet');

  const { t } = useTranslation();

  const {
    searchString,
    searchInputValue,
    onSearchInputChange,
    resetSearchString,
  } = useDebounceSearch({ onSearch });

  useEffect(() => {
    resetState();
    resetSearchString();
  }, [filterKey, resetSearchString, resetState]);

  const selected = React.useMemo(() => {
    if (!value) return [];

    if (Array.isArray(value)) return value;

    return [value];
  }, [value]);

  const selectedName = React.useMemo(
    () => getJoinedWordsWithComma(selected.map(({ label }) => label)),
    [selected],
  );

  const findSelectedUser = useCallback(
    (id: string) => selected.some(({ value }) => value === id),
    [selected],
  );

  const handleOnFilterClick = useCallback(
    (id: string, name: string) => {
      if (isMultiple) {
        const value = findSelectedUser(id)
          ? selected.filter(({ value }) => value !== id)
          : [...selected, { value: id, label: name }];

        onSelect(value);
        return;
      }

      onSelect({ value: id, label: name });
      resetSearchString();
      dropdown?.setIsOpen(false);
    },
    [
      isMultiple,
      selected,
      dropdown,
      onSelect,
      resetSearchString,
      findSelectedUser,
    ],
  );

  return (
    <FilterActionWrapper
      ref={dropdownRef}
      className={classes}
      renderAsPortal={!isPhablet}
      portalClassName="searchable-filter__portal"
      trigerTitle={t(selectedName ? triggerTitle : 'selectAnOption', {
        name: selectedName,
      })}
    >
      <Input
        prefixNode={<SearchIcon />}
        placeholder={t('search')}
        value={searchInputValue}
        onChange={({ target: { value } }) => onSearchInputChange(value)}
      />

      <ul>
        {isLoading ? (
          <li>{t('isLoading')}</li>
        ) : getHasElementsInArray(options) ? (
          options.map(({ value, label }) => (
            <li
              key={value}
              className={classNames({ selected: findSelectedUser(value) })}
              onClick={() => handleOnFilterClick(value, label)}
            >
              <TextHighlighter text={label} highlighter={searchString} />
            </li>
          ))
        ) : searchString ? (
          <li>
            {t('noDataFor')} {searchString}
          </li>
        ) : (
          <li>{t('typeToViewAvailableOptions')}</li>
        )}
      </ul>
    </FilterActionWrapper>
  );
};

export default SearchableFilter;
